import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ROUTE_DQF,
  ROUTE_DQF_ABSTRACT,
  ROUTE_DQF_BACKGROUND_CHECK_PROCESSING,
  ROUTE_DQF_DOCUMENT_UPLOAD,
  ROUTE_DQF_ONTARIO_CVOR_ABSTRACT,
  ROUTE_DQF_POLICE_CERTIFICATE,
  ROUTE_DQF_PRE_EMPLOYMENT_CERTIFICATE,
  ROUTE_DQF_SIGN_DECLARATION
} from 'src/app/shared/routes';
import { DocumentAction } from '../dqf-document-upload/dqf-document-upload.component';
import { AttachedDocuments } from '../models/attachedDocuments';
import { DqfDocument } from '../models/dqfDocument';
import { DriverQualificationService } from '../service/driver-qualification.service';
import {
  EMPLOYMENT_HISTORY_CHECK,
  DQF_DOC_STATUS,
  OPTIONAL_DRIVER_DOCS
} from '../constants';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { finalize } from 'rxjs/operators';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CompleteProfileSheetComponent } from '../complete-profile-sheet/complete-profile-sheet.component';
import { LoadingGifComponent } from 'src/app/shared/loading-gif/loading-gif/loading-gif.component';

const STATUS_REJECTED = 'Rejected';
const STATUS_PENDING = 'Pending';
const DOC_STATUS_DRIVER_REVIEWED = 'DriverReviewed';
const DRIVER_UPLOADS = 'Driver Uploads';
const UPDATE_DQF_DOC_MODEL = {
  attachedDocument: {
    type: '',
    status: DOC_STATUS_DRIVER_REVIEWED
  }
};
const PATH_INCORPORATION = 'incorporation';

@Component({
    selector: 'app-dqf-upload',
    templateUrl: './dqf-upload.component.html',
    styleUrls: ['./dqf-upload.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        LoadingGifComponent
    ]
})
export class DqfUploadComponent implements OnInit {
  heading = null;
  documents: DocumentAction[] = [];
  showStatus = false;
  currentStatus = null;
  currentSelectedData: DocumentAction | string = null;
  order: number;
  licenseProvince = null;
  driverType: string;
  carrierCompanyId: string;
  driver: any;
  paymentOrders: any;
  loading: boolean;
  dqf: DqfDocument;
  driverInfo: any;
  workPlaceSafety: any;
  driverDocuments: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    public driverQualificationService: DriverQualificationService,
    private router: Router,
    private registrationService: RegistrationService,
    private readonly bottomSheet: MatBottomSheet,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.heading = params.type;
    });
    this.driver = this.registrationService.getDriver();
    this.driverDocuments = this.driver?.notification;
    this.workPlaceSafety = this.registrationService.getWorkplaceSafetyStore();
    
    // Check if we have a dqfId, if not, try to get the first available DQF
    if (!this.driverQualificationService.dqfId) {
      this.loadAvailableDqfs();
      return;
    }

    this.getLicense();
    this.getStripeOrders();
    const returnRoute = sessionStorage.getItem('postStripeReturnRoute');

    if (returnRoute) {
      sessionStorage.removeItem('postStripeReturnRoute');
    }
  }

  loadAvailableDqfs(): void {
    this.loading = true;
    this.driverQualificationService
      .getDqfs()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (dqfs: any[]) => {
          if (dqfs && dqfs.length > 0) {
            // Use the first available DQF
            const firstDqf = dqfs[0];
            this.driverQualificationService.dqfId = firstDqf.id;
            this.driverQualificationService.dqfData = firstDqf;
            
            // Now proceed with normal initialization
            this.getLicense();
            this.getStripeOrders();
            const returnRoute = sessionStorage.getItem('postStripeReturnRoute');

            if (returnRoute) {
              sessionStorage.removeItem('postStripeReturnRoute');
            }
          } else {
            // No DQFs available, redirect to DQF list
            this.router.navigate([`${ROUTE_DQF}`]);
          }
        },
        error: (error) => {
          console.error('Error loading DQFs:', error);
          // Redirect to DQF list on error
          this.router.navigate([`${ROUTE_DQF}`]);
        }
      });
  }

  getDqf(): void {
    this.driverQualificationService
      .getDqfById(this.driverQualificationService.dqfId)
      .subscribe((response: DqfDocument) => {
        if (response) {
          this.dqf = response;
          this.carrierCompanyId = response?.carrierCompanyId;
          this.bindDqfResponse(response);
        }
      });
  }
  backAction(): void {
    this.router.navigateByUrl(`${ROUTE_DQF}/${ROUTE_DQF_DOCUMENT_UPLOAD}/${this.driverQualificationService.dqfId}`);
  }

  closeNotification(): void {
    this.showStatus = !this.showStatus;
    this.currentSelectedData = null;
  }

  getStripeOrders(): void {
    this.loading = true;
    this.driverQualificationService
      .getStripeOrders(this.driver.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((response: any) => {
        if (response) {
          this.paymentOrders = response;
          localStorage.setItem('stripePaymentOrders', JSON.stringify(response));
        }
      });
  }

  private navigateToDocument(doc: DocumentAction, callback: Function): void {
    if (doc.status == STATUS_REJECTED) {
      const sheetData = {
        displayName: doc.displayName,
        reason: doc.rejectionReason,
        type: doc.type
      };

      UPDATE_DQF_DOC_MODEL.attachedDocument.type = doc.type;
      this.driverQualificationService.openBottomSheetWithFunction(sheetData, UPDATE_DQF_DOC_MODEL, () => callback());
    } else {
      callback();
    }
  }

  private bindDqfResponse(response: DqfDocument): void {
    if (this.heading === DRIVER_UPLOADS && response?.attachedDocuments) {
      this.documents = this.bindDriverAttachments(response.attachedDocuments);
    } else {
      this.documents = [];
    }
    
    if (this.documents && this.documents.length > 0) {
      this.documents.sort((a, b) => a.order - b.order);
    }
    
    // Force change detection
    this.cdr.detectChanges();
  }

  private bindDriverAttachments(attachedDocuments: AttachedDocuments[]): DocumentAction[] {
    const driver = this.registrationService.getDriver();
    const companyDriver = driver?.companyAssociations?.find(d => d.id.includes(this.carrierCompanyId));
    const driverType = driver?.companyDriverType || undefined;
    this.driverType = companyDriver ? companyDriver.driverType : driverType;
    const documents: DocumentAction[] = this.driverQualificationService.bindDriverAttachments(
      attachedDocuments,
      this.licenseProvince,
      this.driverType,
      this.navigateToDocument.bind(this),
      this.navigateToSignDeclaration.bind(this),
      this.navigateToAbstractDoc.bind(this),
      this.navigateToPoliceCertificateBasedOnAction.bind(this),
      this.navigateToPreEmploymentScreeningReport.bind(this),
      this.navigateToEmploymentHistoryCheck.bind(this),
      this.navigateToOntarioCvorAbstractDoc.bind(this)
    );
    return documents;
  }


  //Driver
  private navigateToOntarioCvorAbstractDoc() {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_ONTARIO_CVOR_ABSTRACT}`]);
  }
  private navigateToAbstractDoc() {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_ABSTRACT}`]);
  }
  private navigateToPoliceClearanceCertificate() {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_POLICE_CERTIFICATE}`]);
  }
  private navigateToBackgroundProcessingRequest(documentType: string, initiateStripe?: boolean) {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_BACKGROUND_CHECK_PROCESSING}`], {
      // queryParams: {
      //   documentType,
      //   initiateStripe
      // }
      queryParams: { documentType: documentType, stripe: initiateStripe }
    });
  }
  private navigateToPreEmploymentScreeningReport(): void {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_PRE_EMPLOYMENT_CERTIFICATE}`]);
  }

  private navigateToSignDeclaration(): void {
    const isDriverOwnerOperator = this.driverType?.toLowerCase().includes('owneroperator') || false;
    const isWorkPlaceSafetyExists = !!this.workPlaceSafety && Object.keys(this.workPlaceSafety).length > 0;
    if (isDriverOwnerOperator && !isWorkPlaceSafetyExists) {
      const completeIncorporation = true;
      const dialogRef = this.bottomSheet.open(CompleteProfileSheetComponent, {
        panelClass: 'signature-panel',
        disableClose: true,
        data: { completeIncorporation: completeIncorporation }
      });
      dialogRef.afterDismissed().subscribe(res => {
        this.router.navigate([`${PATH_INCORPORATION}`]);
      });
      return;
    } else {
      this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_SIGN_DECLARATION}`]);
    }
    // uncomment to enable hello Sign
    // this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_DECLARATION_SIGN}`]);
  }
  private navigateToEmploymentHistoryCheck(): void {
    this.router.navigate([`${ROUTE_DQF}/${ROUTE_DQF_SIGN_DECLARATION}`], {
      queryParams: { type: EMPLOYMENT_HISTORY_CHECK }
    });
  }
  private navigateToPoliceCertificateBasedOnAction(info: any) {
    const hasUploadedDocument = this.driverDocuments.some(
      doc => doc.dqfId === this.dqf.id && doc.type === 'PoliceClearance' && doc.status === 'uploaded'
    );
    const requiresReupload =
      info.status === STATUS_PENDING ||
      info.status === STATUS_REJECTED ||
      info.isRequirementsCompleted === false;

    this.navigateToDocument(info, () => {
      if (info['action'] === 'collect') {
        const doesDriverHavePccOrder = this.driverDocuments.find(
          doc => doc.dqfId === this.dqf.id && doc.type === 'PoliceClearance'
        );
        if (doesDriverHavePccOrder) {
          this.navigateToBackgroundProcessingRequest('Criminal Record (Background) Check');
        } else {
          this.navigateToBackgroundProcessingRequest('Criminal Record (Background) Check', true);
        }
      } else if (hasUploadedDocument && !requiresReupload) {
        this.navigateToPoliceClearanceCertificate();
      } else if (requiresReupload || !hasUploadedDocument) {
        this.navigateToPoliceClearanceCertificate();
      } else {
        this.navigateToBackgroundProcessingRequest('Criminal Record (Background) Check');
      }
    });
  }


  private getLicense(): void {
    const license = this.registrationService.getLicenseStore();
    if (license) {
      this.licenseProvince = license.issuingProvince?.toLowerCase();
    }
    this.getDqf();
  }

  // Helper methods for card styling
  getStatusClass(doc: DocumentAction): string {
    // Handle rejected documents
    if (doc.status === DQF_DOC_STATUS.REJECTED) {
      return 'status-rejected';
    }
    
    // Handle processing documents
    if (doc.status === DQF_DOC_STATUS.PROCESSING) {
      return 'status-pending';
    }

    if (doc.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED) {
      return 'status-requested';
    }
    
    // Handle completed documents - prioritize isRequirementsCompleted
    if (doc.isRequirementsCompleted) {
      return 'status-completed';
    }

    if (this.isOptionalDriverDocumentSkipped(doc)) {
      return 'status-optional';
    }
    
    // Handle pending documents
    if (doc.status === DQF_DOC_STATUS.PENDING) {
      return 'status-pending';
    }
    
    // Handle specific completed statuses even without isRequirementsCompleted flag
    if (doc.status === DQF_DOC_STATUS.DRIVER_REVIEWED || 
        doc.status === DQF_DOC_STATUS.DRIVER_SIGNED || 
        doc.status === DQF_DOC_STATUS.ACCEPTED) {
      return 'status-completed';
    }
    
    return 'status-incomplete';
  }

  getStatusIcon(doc: DocumentAction): string {
    // Handle rejected documents
    if (doc.status === DQF_DOC_STATUS.REJECTED) {
      return 'error';
    }
    
    // Handle processing documents
    if (doc.status === DQF_DOC_STATUS.PROCESSING) {
      return 'schedule';
    }

    if (doc.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED) {
      return 'schedule';
    }
    
    // Handle completed documents - prioritize isRequirementsCompleted
    if (doc.isRequirementsCompleted) {
      return 'check_circle';
    }

    if (this.isOptionalDriverDocumentSkipped(doc)) {
      return 'info';
    }
    
    // Handle pending documents
    if (doc.status === DQF_DOC_STATUS.PENDING) {
      return 'schedule';
    }
    
    // Handle specific completed statuses even without isRequirementsCompleted flag
    if (doc.status === DQF_DOC_STATUS.DRIVER_REVIEWED || 
        doc.status === DQF_DOC_STATUS.DRIVER_SIGNED || 
        doc.status === DQF_DOC_STATUS.ACCEPTED) {
      return 'check_circle';
    }
    
    return 'radio_button_unchecked';
  }

  getStatusIconColor(doc: DocumentAction): string {
    // Handle rejected documents
    if (doc.status === DQF_DOC_STATUS.REJECTED) {
      return '#ef4444';
    }
    
    // Handle processing documents
    if (doc.status === DQF_DOC_STATUS.PROCESSING) {
      return '#f59e0b';
    }

    if (doc.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED) {
      return '#f59e0b';
    }
    
    // Handle completed documents - prioritize isRequirementsCompleted
    if (doc.isRequirementsCompleted) {
      return '#10b981';
    }

    if (this.isOptionalDriverDocumentSkipped(doc)) {
      return '#f59e0b';
    }
    
    // Handle pending documents
    if (doc.status === DQF_DOC_STATUS.PENDING) {
      return '#f59e0b';
    }
    
    // Handle specific completed statuses even without isRequirementsCompleted flag
    if (doc.status === DQF_DOC_STATUS.DRIVER_REVIEWED || 
        doc.status === DQF_DOC_STATUS.DRIVER_SIGNED || 
        doc.status === DQF_DOC_STATUS.ACCEPTED) {
      return '#10b981';
    }
    
    return '#9ca3af';
  }

  getDocumentIcon(doc: DocumentAction): string {
    const iconMap: { [key: string]: string } = {
      // By document type - only ball-pen replaced with alternative
      'declarationForm': 'document',
      'Declaration': 'document',
      'Abstract': 'accident-record-icon',
      'PoliceClearance': 'car-icon',
      'PreEmploymentScreening': 'zoom-icon',
      'EmploymentHistoryCheck': 'document',
      'DriverLicense': 'license-info-icon',
      'PersonalInformation': 'profile-info-icon',
      'ResidenceHistory': 'residence-icon',
      'DriverExperience': 'driving-exp-icon',
      'AccidentsHistory': 'accident-record-icon',
      'TrafficConvictionsHistory': 'accident-record-icon',
      'OnDutyHours': 'on-duty-icon',
      // By display name (spaces removed)
      'PoliceClearanceCertificate': 'car-icon',
      'PreEmploymentScreeningReport': 'zoom-icon'
    };
    
    // Use same fallback logic as DQF document upload component
    return iconMap[doc.type] || iconMap[doc.displayName?.replace(/\s+/g, '')] || 'folder-icon';
  }


  getDocumentIconColor(doc: DocumentAction): string {
    const colorMap: { [key: string]: string } = {
      'declarationForm': '#8b5cf6',
      'Abstract': '#06b6d4',
      'PoliceClearance': '#10b981',
      'PreEmploymentScreening': '#3b82f6',
      'EmploymentHistoryCheck': '#f59e0b',
      'DriverLicense': '#ef4444',
      'PersonalInformation': '#6366f1',
      'ResidenceHistory': '#84cc16',
      'DriverExperience': '#f97316',
      'AccidentsHistory': '#eab308',
      'TrafficConvictionsHistory': '#dc2626',
      'OnDutyHours': '#8b5cf6'
    };
    return colorMap[doc.type] || '#6b7280';
  }

  getStatusText(doc: DocumentAction): string {
    // Handle rejected documents
    if (doc.status === DQF_DOC_STATUS.REJECTED) {
      return 'REJECTED';
    }
    
    // Handle processing documents
    if (doc.status === DQF_DOC_STATUS.PROCESSING) {
      return 'PROCESSING';
    }

    if (doc.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED) {
      return 'REQUESTED CARRIER';
    }
    
    // Handle completed documents - prioritize isRequirementsCompleted
    if (doc.isRequirementsCompleted) {
      return 'COMPLETED';
    }

    if (this.isOptionalDriverDocumentSkipped(doc)) {
      return 'OPTIONAL';
    }

    // Handle pending documents
    if (doc.status === DQF_DOC_STATUS.PENDING) {
      return 'COMPLETE THIS SECTION';
    }

    // Handle specific completed statuses even without isRequirementsCompleted flag
    if (doc.status === DQF_DOC_STATUS.DRIVER_REVIEWED ||
        doc.status === DQF_DOC_STATUS.DRIVER_SIGNED ||
        doc.status === DQF_DOC_STATUS.ACCEPTED) {
      return 'COMPLETED';
    }

    return 'TAP TO GET STARTED';
  }

  getStatusTextColor(doc: DocumentAction): string {
    // Handle rejected documents
    if (doc.status === DQF_DOC_STATUS.REJECTED) {
      return '#dc2626';
    }
    
    // Handle processing documents
    if (doc.status === DQF_DOC_STATUS.PROCESSING) {
      return '#d97706';
    }

    if (doc.status === DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED) {
      return '#f59e0b';
    }
    
    // Handle completed documents - prioritize isRequirementsCompleted
    if (doc.isRequirementsCompleted) {
      return '#059669';
    }

    if (this.isOptionalDriverDocumentSkipped(doc)) {
      return '#f59e0b';
    }
    
    // Handle pending documents
    if (doc.status === DQF_DOC_STATUS.PENDING) {
      return '#d97706';
    }
    
    // Handle specific completed statuses even without isRequirementsCompleted flag
    if (doc.status === DQF_DOC_STATUS.DRIVER_REVIEWED || 
        doc.status === DQF_DOC_STATUS.DRIVER_SIGNED || 
        doc.status === DQF_DOC_STATUS.ACCEPTED) {
      return '#059669';
    }
    
    return '#6b7280';
  }

  private isOptionalDriverDocumentSkipped(doc: { type?: string; status?: string; isRequirementsCompleted?: boolean }): boolean {
    return (
      !!doc?.type &&
      (OPTIONAL_DRIVER_DOCS as readonly string[]).includes(doc.type) &&
      doc.status === DQF_DOC_STATUS.PENDING &&
      doc.isRequirementsCompleted !== true
    );
  }
}
